import React, { useEffect, useState } from 'react'
import { Card, Row, Col, Tag, Button, Table, Space, Descriptions, Result, Divider, Tooltip } from 'antd'
import {
  ArrowLeftOutlined, EditOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, ThunderboltOutlined, UserOutlined, CalendarOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { meterReadingStore, MeterReadingRow } from './meterReadingStore'
import { MeterReadingStatus } from '@/types'
import { useMasterData } from '@/context/MasterDataContext'
import { authStore } from '@/context/auth'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import './MeterReading.css'

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  [MeterReadingStatus.DRAFT]:        { color: 'default',    icon: <FileTextOutlined />,       label: 'Draft' },
  [MeterReadingStatus.SUBMITTED]:    { color: 'processing', icon: <ClockCircleOutlined />,     label: 'Submitted' },
  [MeterReadingStatus.UNDER_REVIEW]: { color: 'warning',    icon: <ClockCircleOutlined />,     label: 'Under Review' },
  [MeterReadingStatus.APPROVED]:     { color: 'success',    icon: <CheckCircleOutlined />,     label: 'Approved' },
  [MeterReadingStatus.REJECTED]:     { color: 'error',      icon: <CloseCircleOutlined />,     label: 'Rejected' },
}

const SLOT_CAT: Record<string, string> = {
  'off-peak': 'Off-Peak',
  'normal':   'Normal',
  'peak':     'Peak',
}

const MeterReadingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { generators, consumers, slots: masterSlots } = useMasterData()
  const isAdmin = authStore.isAdmin()
  const [, setTick] = useState(0)

  useEffect(() => {
    const bump = () => setTick(t => t + 1)
    meterReadingStore.refresh().then(bump)
    window.addEventListener(meterReadingStore.eventName, bump)
    return () => window.removeEventListener(meterReadingStore.eventName, bump)
  }, [id])

  const reading = id ? meterReadingStore.get(id) : undefined

  if (!meterReadingStore.loaded && !reading) {
    return <TableSkeleton columns={4} />
  }

  if (!reading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Result
          status="404"
          title="Reading Not Found"
          subTitle={`No meter reading record found for ID: ${id}`}
          extra={
            <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate('/meter-readings')}>
              Back to Meter Readings
            </Button>
          }
        />
      </div>
    )
  }

  const isGen = reading.type === 'generator'
  const genObj = isGen ? generators.find(g => g.id === reading.entityId) : undefined
  const conObj = !isGen ? consumers.find(c => c.id === reading.entityId) : undefined
  const entityName = reading.entityName || genObj?.name || conObj?.name || reading.entityId

  const start = dayjs(reading.startDate)
  const end = dayjs(reading.endDate)
  const days = end.diff(start, 'day') + 1

  const statusCfg = STATUS_CONFIG[reading.status] || { color: 'default', icon: null, label: reading.status }

  const kpiCards = [
    {
      label: 'Total Net Units',
      value: (
        <>
          {Number(reading.totalUnits || 0).toLocaleString('en-IN')}
          <span style={{ fontSize: 13, fontWeight: 600, color: '#8A93A6', marginLeft: 4 }}>kWh</span>
        </>
      ),
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#001A4D' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: '#001A4D',
    },
    {
      label: 'Reading Period',
      value: `${start.format('DD MMM YYYY')}`,
      subtext: `${days} Days (to ${end.format('DD MMM YYYY')})`,
      icon: <CalendarOutlined style={{ fontSize: 24, color: '#2563EB' }} />,
      iconBg: 'rgba(37,99,235,0.08)',
      borderColor: '#2563EB',
    },
    {
      label: 'Entity Type',
      value: isGen ? 'Generator' : 'Consumer',
      subtext: reading.plantName || reading.entityId,
      icon: isGen ? <ThunderboltOutlined style={{ fontSize: 24, color: '#F59E0B' }} /> : <UserOutlined style={{ fontSize: 24, color: '#059669' }} />,
      iconBg: isGen ? 'rgba(245,158,11,0.08)' : 'rgba(5,150,105,0.08)',
      borderColor: isGen ? '#F59E0B' : '#059669',
    },
    {
      label: 'Status',
      value: statusCfg.label,
      icon: statusCfg.icon,
      iconBg: 'rgba(0,26,77,0.05)',
      borderColor: '#001A4D',
    },
  ]

  const slotColumns = [
    {
      title: 'Slot Code',
      dataIndex: 'slotName',
      key: 'slotName',
      width: 100,
      render: (name: string) => (
        <span style={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#001A4D' }}>
          {name}
        </span>
      ),
    },
    {
      title: 'Category',
      key: 'category',
      width: 140,
      render: (_: any, r: any) => {
        const master = masterSlots.find(s => s.slotCode === r.slotName)
        const cat = master?.category || 'normal'
        return (
          <Tag style={{ borderRadius: 6, fontWeight: 600 }}>
            {SLOT_CAT[cat] || cat}
          </Tag>
        )
      },
    },
    {
      title: 'Initial Reading (kWh)',
      dataIndex: 'initial',
      key: 'initial',
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {Number(val || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      title: 'Final Reading (kWh)',
      dataIndex: 'final',
      key: 'final',
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
          {Number(val || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      title: 'Net Units (kWh)',
      dataIndex: 'units',
      key: 'units',
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#1677ff' }}>
          {Number(val || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
  ]

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* ── Top Navigation Bar ───────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Space size={12}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/meter-readings')}>
            Back to Readings
          </Button>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0B1426' }}>
              Reading: <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#1677ff' }}>{reading.id}</span>
            </h2>
            <div style={{ fontSize: 12, color: '#8A93A6', marginTop: 2 }}>
              {entityName} {reading.plantName ? `· ${reading.plantName}` : ''}
            </div>
          </div>
        </Space>
        <Space>
          <Tag icon={statusCfg.icon} color={statusCfg.color} style={{ fontSize: 13, padding: '4px 12px', borderRadius: 6, fontWeight: 700 }}>
            {statusCfg.label}
          </Tag>
          <Tooltip title={reading.status === MeterReadingStatus.APPROVED && !isAdmin ? "Only Admin can edit approved meter readings" : "Edit Reading"}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="large"
              disabled={reading.status === MeterReadingStatus.APPROVED && !isAdmin}
              onClick={() => navigate(`/meter-readings/${reading.id}/edit`)}
              style={{ fontWeight: 700 }}
            >
              Edit Reading
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* ── KPI Strip ────────────────────────────────────────── */}
      <KpiCardRow cards={kpiCards} />

      <Row gutter={[16, 16]}>
        {/* ── Left Column: Reading Details & Slot Breakdown ──── */}
        <Col xs={24} lg={16}>
          {/* Slot Breakdown */}
          <Card
            title={
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0B1426' }}>
                <ThunderboltOutlined style={{ marginRight: 8, color: '#001A4D' }} />
                Slot-Wise Energy Breakdown
              </span>
            }
            style={{ borderRadius: 12, marginBottom: 16 }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              dataSource={reading.slots || []}
              columns={slotColumns}
              rowKey="slotName"
              pagination={false}
              summary={(pageData) => {
                const totalUnits = pageData.reduce((sum, row) => sum + Number(row.units || 0), 0)
                return (
                  <Table.Summary.Row style={{ background: '#F8FAFC' }}>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <strong style={{ fontSize: 13, color: '#0B1426' }}>Total Net Allocated Units</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <strong style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 15, color: '#1677ff' }}>
                        {totalUnits.toLocaleString('en-IN')} kWh
                      </strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )
              }}
            />
          </Card>
        </Col>

        {/* ── Right Column: Info & Audit Cards ───────────────── */}
        <Col xs={24} lg={8}>
          <Card title="Plant & Entity Info" style={{ borderRadius: 12, marginBottom: 16 }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Entity Name">
                <strong>{entityName}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Entity ID">
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{reading.entityId}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Plant Name">
                {reading.plantName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="HT Service No.">
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{(reading as any).htNumber || '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Reading Type">
                <Tag color={isGen ? 'orange' : 'blue'}>{isGen ? 'Generator' : 'Consumer'}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Approval & Audit Trail" style={{ borderRadius: 12 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Submitted By">
                <strong>{reading.submittedBy || '—'}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Submitted On">
                {reading.submittedAt ? dayjs(reading.submittedAt).format('DD MMM YYYY, hh:mm A') : '—'}
              </Descriptions.Item>
              {reading.approvedBy && (
                <Descriptions.Item label="Approved By">
                  <span style={{ color: '#059669', fontWeight: 600 }}>{reading.approvedBy}</span>
                </Descriptions.Item>
              )}
              {reading.approvedAt && (
                <Descriptions.Item label="Approved On">
                  {dayjs(reading.approvedAt).format('DD MMM YYYY')}
                </Descriptions.Item>
              )}
              {reading.rejectionReason && (
                <Descriptions.Item label="Rejection Reason">
                  <span style={{ color: '#DC2626', fontWeight: 600 }}>{reading.rejectionReason}</span>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider style={{ margin: '16px 0 12px' }} />

            <div style={{ display: 'flex', gap: 8 }}>
              <Tooltip title={reading.status === MeterReadingStatus.APPROVED && !isAdmin ? "Only Admin can edit approved meter readings" : "Edit Reading Page"}>
                <Button
                  block
                  type="primary"
                  icon={<EditOutlined />}
                  disabled={reading.status === MeterReadingStatus.APPROVED && !isAdmin}
                  onClick={() => navigate(`/meter-readings/${reading.id}/edit`)}
                >
                  Edit Page
                </Button>
              </Tooltip>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default MeterReadingDetail
